import React, { useRef } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, useInView } from 'framer-motion';
import { ArrowRight, CheckCircle2, Clock, Euro, Users } from 'lucide-react';
import SEO from '../components/SEO';
import { PageHero } from '../components/PageComponents';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { specialismes } from '../data/specialismes';

const FadeIn = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const SpecialismePage = () => {
  const { slug } = useParams();
  const spec = specialismes[slug];

  if (!spec) return <Navigate to="/404" replace />;

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: spec.title,
    provider: {
      '@type': 'EmploymentAgency',
      name: 'Staffing Force',
      url: 'https://www.staffingforce.nl',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Johan Huizingalaan 400',
        addressLocality: 'Amsterdam',
        postalCode: '1066 JS',
        addressCountry: 'NL',
      },
      telephone: '+31205811000',
    },
    areaServed: [
      { '@type': 'Country', name: 'Nederland' },
      { '@type': 'City', name: 'Amsterdam' },
      { '@type': 'City', name: 'Rotterdam' },
      { '@type': 'City', name: 'Utrecht' },
      { '@type': 'City', name: 'Den Haag' },
    ],
    description: spec.metaDescription,
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: spec.faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <main className="pt-28 min-h-screen">
      <SEO
        title={spec.metaTitle}
        description={spec.metaDescription}
        keywords={spec.keywords.join(', ')}
        image={spec.heroImage}
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <PageHero
        label={spec.discipline}
        title={spec.h1}
        description={spec.intro.slice(0, 200) + '...'}
        image={spec.heroImage}
      />

      {/* Intro + metrics */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8">
          <FadeIn>
            <p className="text-gray-700 leading-relaxed text-base md:text-lg max-w-3xl">{spec.intro}</p>
          </FadeIn>

          <FadeIn delay={0.2}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mt-12">
              {spec.metrics.map((m, i) => (
                <div
                  key={i}
                  className="bg-gray-50 border border-gray-100 rounded-2xl p-5 md:p-6 text-center"
                >
                  <div className="text-2xl md:text-4xl font-bold text-[#00897B]">{m.value}</div>
                  <div className="text-xs md:text-sm text-gray-600 mt-2 leading-tight">{m.label}</div>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Tarieven tabel */}
      {spec.tarievenTabel && (
        <section className="py-12 md:py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
            <FadeIn>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                Tarieven {spec.title.toLowerCase()} (2026)
              </h2>
              <p className="text-gray-600 mb-6 text-sm md:text-base">
                Indicatie van marktconforme uurtarieven, afhankelijk van ervaring en complexiteit.
              </p>
            </FadeIn>
            <FadeIn delay={0.1}>
              <div className="overflow-x-auto">
                <table className="w-full bg-white border border-gray-200 rounded-xl overflow-hidden text-sm">
                  <thead>
                    <tr className="bg-gray-100 text-left">
                      <th className="px-4 py-3 font-semibold text-gray-900">Niveau</th>
                      <th className="px-4 py-3 font-semibold text-gray-900">Tarief</th>
                      <th className="px-4 py-3 font-semibold text-gray-900 hidden md:table-cell">
                        Toelichting
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {spec.tarievenTabel.map((t, i) => (
                      <tr key={i} className="border-t border-gray-100">
                        <td className="px-4 py-3 text-gray-700 font-medium">{t.niveau}</td>
                        <td className="px-4 py-3 font-semibold text-[#00897B]">{t.tarief}</td>
                        <td className="px-4 py-3 text-gray-600 hidden md:table-cell">{t.toelichting}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </FadeIn>
          </div>
        </section>
      )}

      {/* Content sections */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
          {spec.sections.map((sec, i) => (
            <FadeIn key={i} delay={0.05 * i} className="mb-10 md:mb-14 last:mb-0">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{sec.heading}</h2>
              <p className="text-gray-700 leading-relaxed text-base">{sec.body}</p>
              {sec.list && (
                <ul className="mt-5 space-y-2.5">
                  {sec.list.map((item, j) => (
                    <li key={j} className="flex gap-3 text-gray-700 text-base">
                      <CheckCircle2 size={20} className="text-[#00897B] flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </FadeIn>
          ))}
        </div>
      </section>

      {/* Case study */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
          <FadeIn>
            <span className="text-[#00897B] text-xs md:text-sm font-semibold tracking-widest uppercase">
              Klantcase
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-3 mb-8">Praktijkvoorbeeld</h2>
          </FadeIn>
          <FadeIn delay={0.1}>
            <div className="bg-[#0d1b2a] text-white rounded-2xl p-6 md:p-10 grid md:grid-cols-2 gap-6 md:gap-10">
              <div>
                <div className="flex items-center gap-2 text-[#00bfa5] text-xs font-semibold uppercase tracking-widest mb-2">
                  <Users size={16} /> Sector
                </div>
                <p className="text-white/90 mb-6">{spec.caseStudy.sector}</p>

                <div className="flex items-center gap-2 text-[#00bfa5] text-xs font-semibold uppercase tracking-widest mb-2">
                  <Clock size={16} /> Doorlooptijd
                </div>
                <p className="text-white/90">{spec.caseStudy.doorlooptijd}</p>
              </div>
              <div>
                <div className="flex items-center gap-2 text-[#00bfa5] text-xs font-semibold uppercase tracking-widest mb-2">
                  Uitdaging
                </div>
                <p className="text-white/90 mb-6">{spec.caseStudy.challenge}</p>

                <div className="flex items-center gap-2 text-[#00bfa5] text-xs font-semibold uppercase tracking-widest mb-2">
                  <Euro size={16} /> Resultaat
                </div>
                <p className="text-white/90">{spec.caseStudy.resultaat}</p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
          <FadeIn>
            <span className="text-[#00897B] text-xs md:text-sm font-semibold tracking-widest uppercase">
              Veelgestelde vragen
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-3 mb-8">
              {spec.title} — antwoorden op uw vragen
            </h2>
          </FadeIn>
          <FadeIn delay={0.1}>
            <Accordion type="single" collapsible className="space-y-3">
              {spec.faqs.map((faq, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="bg-gray-50 border border-gray-100 rounded-xl px-4 md:px-6 overflow-hidden data-[state=open]:bg-[#00897B]/5 data-[state=open]:border-[#00897B]/20 transition-colors"
                >
                  <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-[#00897B] transition-colors py-5 text-sm md:text-base hover:no-underline">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-700 leading-relaxed pb-5 text-sm md:text-base">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </FadeIn>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-[#0d1b2a]">
        <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 text-center">
          <FadeIn>
            <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
              {spec.title} — direct in gesprek?
            </h2>
            <p className="text-white/70 mb-8 max-w-2xl mx-auto text-sm md:text-base">
              Vertel ons waar u tegenaan loopt. Binnen één werkdag heeft u een eerste reactie van ons team in Amsterdam.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/opdracht-opgeven"
                className="bg-[#00897B] hover:bg-[#00796B] text-white px-6 md:px-8 py-3.5 rounded font-semibold text-sm tracking-wide transition-all duration-300 hover:-translate-y-0.5 inline-flex items-center justify-center gap-2"
              >
                Opdracht opgeven <ArrowRight size={16} />
              </Link>
              <Link
                to="/contact"
                className="border-2 border-white/30 hover:border-white text-white px-6 md:px-8 py-3.5 rounded font-semibold text-sm tracking-wide transition-all duration-300 hover:-translate-y-0.5 inline-flex items-center justify-center"
              >
                Neem contact op
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </main>
  );
};

export default SpecialismePage;
